/**
 * PdfViewerModal
 * Opens supported file formats in a unified in-app preview modal.
 * Uses backend proxying for secure blob access and CORS-safe previewing.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { API_BASE_URL } from '../api/apiConfig';
import './PdfViewerModal.css';

// Point pdfjs at the worker file copied to /public
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/** Strip any SAS query-string from a blob URL */
const cleanBlobUrl = (url) => {
  if (!url) return url;
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.split('?')[0];
  }
};

const getFileExtension = (url) => {
  if (!url) return '';
  const clean = cleanBlobUrl(url);
  const filename = clean.split('/').pop() || '';
  const ext = filename.includes('.') ? filename.split('.').pop() : '';
  return ext.toLowerCase();
};

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);
const TEXT_EXTENSIONS = new Set(['txt', 'md', 'markdown', 'json', 'csv', 'log', 'xml', 'yaml', 'yml']);
const OFFICE_EXTENSIONS = new Set(['docx', 'xlsx', 'pptx']);

const PdfViewerModal = ({ isOpen, onClose, blobUrl, highlightText, accessToken }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomScale, setZoomScale] = useState(1);
  const [loadError, setLoadError] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [imageObjectUrl, setImageObjectUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [officePreviewUrl, setOfficePreviewUrl] = useState(null);
  const [officeLoading, setOfficeLoading] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);
  const [retryKey, setRetryKey] = useState(0);
  const [foundPage, setFoundPage] = useState(null);       // 1-based page that contains highlight
  const [lastTextLayerPage, setLastTextLayerPage] = useState(0); // last page whose text layer rendered
  const pageRefs = useRef([]);
  const bodyRef = useRef(null);
  const ZOOM_STEP = 0.2;
  const ZOOM_MIN = 0.6;
  const ZOOM_MAX = 2.2;

  const fileExtension = useMemo(() => getFileExtension(blobUrl), [blobUrl]);
  const isPdf = fileExtension === 'pdf';
  const isImage = IMAGE_EXTENSIONS.has(fileExtension);
  const isText = TEXT_EXTENSIONS.has(fileExtension);
  const isOffice = OFFICE_EXTENSIONS.has(fileExtension);

  // Build the generic proxy URL — backend downloads blob server-side
  const proxyUrl = useMemo(() => {
    if (!blobUrl) return null;
    const base = cleanBlobUrl(blobUrl);
    return `${API_BASE_URL}/api/documents/file-proxy?blob_url=${encodeURIComponent(base)}`;
  }, [blobUrl]);

  // Pass auth header so the backend accepts the request for PDF loading.
  const pdfFile = useMemo(() => {
    if (!proxyUrl || !isPdf) return null;
    return {
      url: proxyUrl,
      httpHeaders: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    };
  }, [proxyUrl, accessToken, isPdf]);

  // Reset state when modal opens with a new URL.
  useEffect(() => {
    if (isOpen) {
      setNumPages(null);
      setCurrentPage(1);
      setZoomScale(1);
      setLoadError(null);
      setTextContent('');
      setTextLoading(false);
      setImageObjectUrl(null);
      setImageLoading(false);
      setOfficePreviewUrl(null);
      setOfficeLoading(false);
      setFoundPage(null);
      setLastTextLayerPage(0);
      pageRefs.current = [];
    }
  }, [isOpen, blobUrl]);

  useEffect(() => {
    if (!isOpen || !isImage || !proxyUrl) return;

    let cancelled = false;
    let createdObjectUrl = null;

    const loadImage = async () => {
      setImageLoading(true);
      setLoadError(null);
      try {
        const response = await axios.get(proxyUrl, {
          responseType: 'blob',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
        });

        createdObjectUrl = URL.createObjectURL(response.data);
        if (!cancelled) {
          setImageObjectUrl(createdObjectUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(`Failed to preview image: ${err?.message || 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) {
          setImageLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
      if (createdObjectUrl) {
        URL.revokeObjectURL(createdObjectUrl);
      }
    };
  }, [isOpen, isImage, proxyUrl, accessToken, retryKey]);

  // Responsive page width
  useEffect(() => {
    const updateWidth = () => {
      const modalWidth = Math.min(window.innerWidth * 0.85, 900);
      setPageWidth(Math.floor(modalWidth - 48));
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onDocumentLoadSuccess = React.useCallback((pdf) => {
    if (!isPdf) return;
    setNumPages(pdf.numPages);
    setCurrentPage(1);
    setLoadError(null);

    // Upfront text search: iterate every page via pdfjs to find the matching chunk
    if (!highlightText) return;
    const normalize = (s) => s.replace(/\s+/g, '').toLowerCase();
    const raw = highlightText.trim();
    // Build 3 probes from different regions of the chunk — any hit is enough
    const probes = [
      raw.slice(0, 60),
      raw.slice(Math.floor(raw.length / 2) - 30, Math.floor(raw.length / 2) + 30),
      raw.slice(Math.max(0, raw.length - 60)),
    ].map(normalize).filter(p => p.length >= 15);
    if (probes.length === 0) return;

    let cancelled = false;
    (async () => {
      for (let p = 1; p <= pdf.numPages; p++) {
        if (cancelled) return;
        try {
          const page = await pdf.getPage(p);
          const tc = await page.getTextContent();
          const pageNorm = normalize(
            tc.items.filter(i => typeof i.str === 'string').map(i => i.str).join('')
          );
          if (probes.some(probe => pageNorm.includes(probe))) {
            setFoundPage(p);
            return;
          }
        } catch { /* skip unreadable pages */ }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPdf, highlightText]);

  useEffect(() => {
    if (!isOpen || !isText || !proxyUrl) return;

    let cancelled = false;
    const loadText = async () => {
      setTextLoading(true);
      setLoadError(null);
      try {
        const response = await axios.get(proxyUrl, {
          responseType: 'text',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
        });
        if (!cancelled) {
          setTextContent(typeof response.data === 'string' ? response.data : String(response.data));
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(`Failed to preview text file: ${err?.message || 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) {
          setTextLoading(false);
        }
      }
    };

    loadText();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isText, proxyUrl, accessToken, retryKey]);

  useEffect(() => {
    if (!isOpen || !isOffice || !blobUrl) return;

    let cancelled = false;
    const loadOfficePreviewUrl = async () => {
      setOfficeLoading(true);
      setLoadError(null);
      try {
        if (blobUrl.includes('blob.core.windows.net')) {
          const response = await axios.get(`${API_BASE_URL}/api/documents/pdf-sas-url`, {
            params: { blob_url: cleanBlobUrl(blobUrl) },
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
          });
          const sasUrl = response?.data?.sas_url;
          if (!sasUrl) {
            throw new Error('Could not create secure preview URL for this document.');
          }
          if (!cancelled) {
            setOfficePreviewUrl(sasUrl);
          }
        } else if (!cancelled) {
          setOfficePreviewUrl(blobUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(`Failed to preview Office document: ${err?.message || 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) {
          setOfficeLoading(false);
        }
      }
    };

    loadOfficePreviewUrl();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isOffice, blobUrl, accessToken]);

  // Auto-scroll to the found page
  useEffect(() => {
    if (!foundPage || !isPdf) return;
    const timer = setTimeout(() => {
      const target = pageRefs.current[foundPage - 1];
      if (target && bodyRef.current) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setCurrentPage(foundPage);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [foundPage, isPdf]);

  // DOM highlight: once the found page's text layer renders, highlight matching spans
  useEffect(() => {
    if (!foundPage || !highlightText || !isPdf) return;
    if (lastTextLayerPage !== foundPage) return;

    const wrapper = pageRefs.current[foundPage - 1];
    if (!wrapper) return;
    const textLayer = wrapper.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) return;

    const spans = Array.from(textLayer.querySelectorAll('span')).filter(
      s => s.textContent.trim().length > 0
    );
    if (spans.length === 0) return;

    const chunkNorm = highlightText.replace(/\s+/g, ' ').toLowerCase();
    let firstHighlighted = null;

    spans.forEach(span => {
      const t = span.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (t.length >= 4 && chunkNorm.includes(t)) {
        span.classList.add('pdf-chunk-highlight');
        if (!firstHighlighted) firstHighlighted = span;
      }
    });

    if (firstHighlighted && bodyRef.current) {
      const spanRect = firstHighlighted.getBoundingClientRect();
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const relativeTop = spanRect.top - bodyRect.top + bodyRef.current.scrollTop;
      bodyRef.current.scrollTo({
        top: Math.max(0, relativeTop - bodyRef.current.clientHeight / 4),
        behavior: 'smooth'
      });
    }
  }, [foundPage, lastTextLayerPage, highlightText, isPdf]);

  const goToPage = (targetPage) => {
    if (!isPdf || !numPages) return;
    const clamped = Math.min(Math.max(targetPage, 1), numPages);
    const target = pageRefs.current[clamped - 1];

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setCurrentPage(clamped);
  };

  useEffect(() => {
    if (!isOpen || !isPdf || !numPages || !bodyRef.current) {
      return;
    }

    const container = bodyRef.current;

    const handleScroll = () => {
      const top = container.scrollTop;
      let visiblePage = 1;

      pageRefs.current.forEach((pageEl, index) => {
        if (pageEl && pageEl.offsetTop - 24 <= top) {
          visiblePage = index + 1;
        }
      });

      setCurrentPage((prev) => (prev === visiblePage ? prev : visiblePage));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen, isPdf, numPages]);

  const docTitle = blobUrl
    ? decodeURIComponent(cleanBlobUrl(blobUrl).split('/').pop() || 'Document')
    : 'Document';

  const officeEmbedUrl = useMemo(() => {
    if (!officePreviewUrl) return null;
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(officePreviewUrl)}`;
  }, [officePreviewUrl]);

  const handleZoomIn = () => {
    setZoomScale((prev) => Math.min(ZOOM_MAX, +(prev + ZOOM_STEP).toFixed(2)));
  };

  const handleZoomOut = () => {
    setZoomScale((prev) => Math.max(ZOOM_MIN, +(prev - ZOOM_STEP).toFixed(2)));
  };

  const handleZoomReset = () => {
    setZoomScale(1);
  };

  const renderPreviewBody = () => {
    if (loadError) {
      return (
        <div className="pdf-modal-status pdf-modal-error">
          <div className="pdf-modal-error-icon">⚠️</div>
          <div>{loadError}</div>
          <button
            className="pdf-retry-btn"
            onClick={() => { setLoadError(null); setRetryKey((k) => k + 1); }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (isPdf) {
      return (
        <Document
          key={`pdf-${retryKey}-${proxyUrl || 'none'}`}
          file={pdfFile}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(err) => setLoadError(`Failed to render PDF: ${err.message}`)}
          loading={
            <div className="pdf-modal-status">
              <div className="pdf-modal-spinner" />
              <span>Loading document...</span>
            </div>
          }
          error={
            <div className="pdf-modal-status pdf-modal-error">
              <div className="pdf-modal-error-icon">⚠️</div>
              <div>Could not load PDF.</div>
            </div>
          }
        >
          {Array.from({ length: numPages || 0 }, (_, i) => (
            <div
              key={i}
              className="pdf-page-wrapper"
              ref={(el) => {
                pageRefs.current[i] = el;
              }}
            >
              <Page
                pageNumber={i + 1}
                width={Math.max(320, Math.floor(pageWidth * zoomScale))}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                onRenderTextLayerSuccess={highlightText ? () => setLastTextLayerPage(i + 1) : undefined}
              />
            </div>
          ))}
        </Document>
      );
    }

    if (isImage) {
      if (imageLoading || !imageObjectUrl) {
        return (
          <div className="pdf-modal-status">
            <div className="pdf-modal-spinner" />
            <span>Loading image preview...</span>
          </div>
        );
      }

      return (
        <img
          src={imageObjectUrl}
          alt={docTitle}
          className="file-preview-image"
          style={{
            width: `${Math.round(zoomScale * 100)}%`,
            maxWidth: 'none',
            height: 'auto'
          }}
        />
      );
    }

    if (isText) {
      if (textLoading) {
        return (
          <div className="pdf-modal-status">
            <div className="pdf-modal-spinner" />
            <span>Loading text preview...</span>
          </div>
        );
      }

      return <pre className="file-preview-text">{textContent || 'File is empty.'}</pre>;
    }

    if (isOffice) {
      if (officeLoading || !officeEmbedUrl) {
        return (
          <div className="pdf-modal-status">
            <div className="pdf-modal-spinner" />
            <span>Preparing Office preview...</span>
          </div>
        );
      }

      return (
        <iframe
          className="file-preview-iframe"
          title={docTitle}
          src={officeEmbedUrl}
        />
      );
    }

    return (
      <div className="pdf-modal-status">
        <div>This file type is not previewable in-app.</div>
        <a
          className="pdf-open-external-btn"
          href={blobUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in new tab
        </a>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="pdf-modal-overlay" onClick={onClose}>
      <div className="pdf-modal-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="pdf-modal-header">
          <div className="pdf-modal-header-left">
            <div className="pdf-modal-title-block">
              <div className="pdf-modal-title" title={docTitle}>{docTitle}</div>
            </div>
          </div>
          <button className="pdf-modal-close-btn" onClick={onClose} title="Close">
            &#x2715;
          </button>
        </div>

        {isPdf && numPages && !loadError && (
          <div className="pdf-modal-controls" aria-label="PDF navigation controls">
            <div className="pdf-controls-group" aria-label="Page navigation">
              <button
                className="pdf-page-btn"
                onClick={() => goToPage(1)}
                disabled={currentPage <= 1}
                title="First page"
              >
                First
              </button>
              <button
                className="pdf-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                title="Previous page"
              >
                Prev
              </button>
              <div className="pdf-page-indicator">Page {currentPage} of {numPages}</div>
              <button
                className="pdf-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= numPages}
                title="Next page"
              >
                Next
              </button>
              <button
                className="pdf-page-btn"
                onClick={() => goToPage(numPages)}
                disabled={currentPage >= numPages}
                title="Last page"
              >
                Last
              </button>
            </div>

            <div className="pdf-controls-group" aria-label="Zoom controls">
              <button
                className="pdf-page-btn"
                onClick={handleZoomOut}
                disabled={zoomScale <= ZOOM_MIN}
                title="Zoom out"
              >
                -
              </button>
              <div className="pdf-zoom-indicator">{Math.round(zoomScale * 100)}%</div>
              <button
                className="pdf-page-btn"
                onClick={handleZoomIn}
                disabled={zoomScale >= ZOOM_MAX}
                title="Zoom in"
              >
                +
              </button>
              <button
                className="pdf-page-btn"
                onClick={handleZoomReset}
                disabled={zoomScale === 1}
                title="Reset zoom"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {isImage && imageObjectUrl && !loadError && (
          <div className="pdf-modal-controls" aria-label="Image zoom controls">
            <div className="pdf-controls-group" aria-label="Image preview">
              <div className="pdf-page-indicator">Image preview</div>
            </div>

            <div className="pdf-controls-group" aria-label="Zoom controls">
              <button
                className="pdf-page-btn"
                onClick={handleZoomOut}
                disabled={zoomScale <= ZOOM_MIN}
                title="Zoom out"
              >
                -
              </button>
              <div className="pdf-zoom-indicator">{Math.round(zoomScale * 100)}%</div>
              <button
                className="pdf-page-btn"
                onClick={handleZoomIn}
                disabled={zoomScale >= ZOOM_MAX}
                title="Zoom in"
              >
                +
              </button>
              <button
                className="pdf-page-btn"
                onClick={handleZoomReset}
                disabled={zoomScale === 1}
                title="Reset zoom"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Body — preview adapts by file type */}
        <div className="pdf-modal-body" ref={bodyRef}>
          {renderPreviewBody()}
        </div>

      </div>
    </div>
  );
};

export default PdfViewerModal;
